import React, { useState, useEffect } from 'react';
import { CheckCircle, AlertCircle, X, Info, AlertTriangle } from 'lucide-react';

// Toast type styles
const toastStyles = {
  success: {
    bg: 'bg-green-50',
    border: 'border-green-400',
    icon: <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-green-500" />,
    title: 'text-green-800',
    text: 'text-green-700'
  },
  error: {
    bg: 'bg-red-50',
    border: 'border-red-400',
    icon: <AlertCircle className="h-4 w-4 sm:h-5 sm:w-5 text-red-500" />,
    title: 'text-red-800',
    text: 'text-red-700'
  },
  warning: {
    bg: 'bg-yellow-50',
    border: 'border-yellow-400',
    icon: <AlertTriangle className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-500" />,
    title: 'text-yellow-800',
    text: 'text-yellow-700'
  },
  info: {
    bg: 'bg-blue-50',
    border: 'border-blue-400',
    icon: <Info className="h-4 w-4 sm:h-5 sm:w-5 text-blue-500" />,
    title: 'text-blue-800',
    text: 'text-blue-700'
  }
};

/**
 * A mobile-optimized toast notification component
 * 
 * @param {Object} props Component props
 * @param {string} props.type Type of notification ('success', 'error', 'warning', 'info')
 * @param {string} props.title Title text for the toast
 * @param {string} props.message Body text for the toast
 * @param {number} props.duration How long to show the toast in milliseconds
 * @param {Function} props.onClose Function called when toast is closed
 */
const ToastNotification = ({ 
  type = 'info', 
  title, 
  message, 
  duration = 5000, 
  onClose 
}) => {
  const [visible, setVisible] = useState(true);
  const style = toastStyles[type] || toastStyles.info;
  
  useEffect(() => {
    const timer = setTimeout(() => {
      handleClose();
    }, duration);
    
    return () => clearTimeout(timer);
  }, [duration]);
  
  const handleClose = () => {
    setVisible(false);
    // Give time for animation to complete
    setTimeout(() => {
      if (onClose) onClose();
    }, 300);
  };
  
  if (!visible) return null;
  
  return (
    <div 
      className={`w-full mx-4 sm:mx-0 sm:max-w-md ${style.bg} border-l-4 ${style.border} p-3 sm:p-4 rounded-md shadow-md transition-all duration-300 ease-in-out transform translate-x-0 opacity-100`}
      role="alert"
      aria-live="assertive"
    >
      <div className="flex items-start">
        <div className="flex-shrink-0 pt-0.5">
          {style.icon}
        </div>
        <div className="ml-2 sm:ml-3 flex-1">
          {title && (
            <h3 className={`text-xs sm:text-sm font-medium ${style.title}`}>
              {title}
            </h3>
          )}
          {message && (
            <div className={`mt-0.5 sm:mt-1 text-xs sm:text-sm ${style.text}`}>
              <p>{message}</p>
            </div>
          )}
        </div>
        <div className="ml-2 sm:ml-4 flex-shrink-0 flex">
          <button
            onClick={handleClose}
            className="mobile-touch-target inline-flex text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            aria-label="Close notification"
          >
            <X className="h-4 w-4 sm:h-5 sm:w-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ToastNotification;